// contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../components/Firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../components/Firebase';

const AuthContext = createContext();

const PRODUCTION_API_URL = 'https://symuliga.pl';

const getVipModeFlagName = () => {
  if (process.env.REACT_APP_VIP_MODE_FLAG) {
    return process.env.REACT_APP_VIP_MODE_FLAG;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname && hostname !== 'symuliga.pl' && hostname !== 'www.symuliga.pl') {
    return 'vipModeEnabledTest';
  }

  const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return apiUrl === PRODUCTION_API_URL ? 'vipModeEnabled' : 'vipModeEnabledTest';
};

export function AuthProvider({ children }) {
  const [userInfo, setUserInfo] = useState(null);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [vipModeEnabled, setVipModeEnabled] = useState(false);

  useEffect(() => {
    const vipModeFlagName = getVipModeFlagName();

    const unsubscribeFlag = onSnapshot(doc(db, 'feature_flags', 'global'), (snap) => {
      if (snap.exists()) {
        setVipModeEnabled(snap.data()[vipModeFlagName] === true);
      } else {
        setVipModeEnabled(false);
      }
    });

    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      setLoading(true);

      if (user) {
        setUserInfo({
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
        });

        try {
          const token = await user.getIdToken(true);
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (billingErr) {
            console.warn('Nie udało się zsynchronizować statusu subskrypcji:', billingErr);
          }

          const idTokenResult = await user.getIdTokenResult(true);
          setRoles(idTokenResult.claims || {});
        } catch (err) {
          console.error('Błąd podczas pobierania claims:', err);
          setRoles({});
        }
      } else {
        setUserInfo(null);
        setRoles({});
      }

      setLoading(false);
    });

    return () => {
      unsubscribeFlag();
      unsubscribeAuth();
    };
  }, []);

  const refreshRoles = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setRoles({});
      return {};
    }

    const idTokenResult = await currentUser.getIdTokenResult(true);
    const refreshedRoles = idTokenResult.claims || {};
    setRoles(refreshedRoles);
    return refreshedRoles;
  }, []);

  const hasRole = (role) => !!roles[role];

  const value = {
    user: userInfo,
    loading,
    roles,
    refreshRoles,
    hasRole,
    isAdmin: hasRole('admin'),
    isVip: hasRole('vip'),
    vipModeEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth musi być używany wewnątrz AuthProvider');
  }
  return context;
};
